import { useCallback, useEffect, useMemo, useState } from "react";

import { authFetch } from "@/api/client";
import { useStore } from "@/state/store";

interface AuditLogView {
  id: string;
  actor_user_id: string | null;
  action: string;
  target_kind: string;
  target_id: string | null;
  payload: Record<string, unknown> | unknown;
  reason: string | null;
  created_at: string;
}

interface AuditLogResponse {
  items: AuditLogView[];
  next_cursor?: string | null;
}

const TARGET_KINDS = ["all", "market", "user", "withdrawal", "order"] as const;

/**
 * Stream eventi append-only del backoffice.
 *
 * Layout volutamente non tabellare: gli eventi hanno densità informativa
 * variabile (alcuni hanno payload corposi, altri minimi) e una timeline a
 * lista è più leggibile. Il payload completo è in collapsable JSON.
 *
 * Filtri:
 *  - action: match esatto o substring (lato backend);
 *  - target_kind: dropdown discreto (market/user/withdrawal/order/all);
 *  - actor_user_id: filtro per attore (string).
 *
 * Paginazione cursor-based: il backend ritorna `next_cursor` quando ci sono
 * altri risultati. La UI offre "Carica altri" invece di pagine numerate per
 * coerenza con uno stream temporale.
 */
export function AuditLogPage() {
  const showToast = useStore((s) => s.showToast);

  const [entries, setEntries] = useState<AuditLogView[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [actionFilter, setActionFilter] = useState("");
  const [targetKindFilter, setTargetKindFilter] =
    useState<(typeof TARGET_KINDS)[number]>("all");
  const [actorFilter, setActorFilter] = useState("");

  const [expanded, setExpanded] = useState<string | null>(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("limit", "50");
    if (actionFilter.trim()) params.set("action", actionFilter.trim());
    if (targetKindFilter !== "all") {
      params.set("target_kind", targetKindFilter);
    }
    if (actorFilter.trim()) {
      params.set("actor_user_id", actorFilter.trim());
    }
    return params.toString();
  }, [actionFilter, targetKindFilter, actorFilter]);

  const load = useCallback(
    async (mode: "reset" | "append") => {
      if (mode === "reset") {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setErr(null);
      try {
        const url =
          mode === "append" && cursor
            ? `/v1/admin/audit-log?${queryString}&cursor=${encodeURIComponent(cursor)}`
            : `/v1/admin/audit-log?${queryString}`;
        const res = await authFetch(url);
        if (!res.ok) throw new Error(`audit-log ${res.status}`);
        const data = (await res.json()) as AuditLogResponse;
        setEntries((prev) =>
          mode === "append" ? [...prev, ...(data.items ?? [])] : data.items ?? [],
        );
        setCursor(data.next_cursor ?? null);
        setHasMore(!!data.next_cursor);
      } catch (e) {
        setErr(e instanceof Error ? e.message : "errore sconosciuto");
        if (mode === "reset") setEntries([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [queryString, cursor],
  );

  // Quando cambia un filtro: reset + reload. Il cursor viene scartato.
  useEffect(() => {
    setCursor(null);
    setHasMore(false);
    setExpanded(null);
    load("reset");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  function resetFilters() {
    setActionFilter("");
    setTargetKindFilter("all");
    setActorFilter("");
  }

  function loadMore() {
    if (!hasMore || loadingMore) return;
    load("append");
  }

  function copyToClipboard(text: string) {
    if (!navigator?.clipboard) return;
    navigator.clipboard.writeText(text).then(
      () => showToast({ kind: "info", message: "Copiato negli appunti." }),
      () => showToast({ kind: "error", message: "Copia fallita." }),
    );
  }

  return (
    <div className="flex flex-col gap-4 max-w-[1200px]">
      <header>
        <h1 className="text-[22px] font-semibold">Audit Log</h1>
        <p className="text-[13px] text-muted mt-1">
          Cronologia append-only delle operazioni amministrative.
        </p>
      </header>

      <div className="card flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 flex-1 min-w-[180px]">
          <span className="text-[11.5px] text-muted">Action</span>
          <input
            className="input"
            placeholder="es. approve_withdrawal"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 w-[160px]">
          <span className="text-[11.5px] text-muted">Target</span>
          <select
            className="input"
            value={targetKindFilter}
            onChange={(e) =>
              setTargetKindFilter(
                e.target.value as (typeof TARGET_KINDS)[number],
              )
            }
          >
            {TARGET_KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <span className="text-[11.5px] text-muted">Actor user id</span>
          <input
            className="input font-mono text-[11.5px]"
            placeholder="UUID dell'attore"
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="btn-secondary"
          onClick={resetFilters}
          disabled={
            !actionFilter && targetKindFilter === "all" && !actorFilter
          }
        >
          Reset
        </button>
      </div>

      {err && (
        <div className="card text-[12.5px] text-no">
          Caricamento audit log fallito: {err}
        </div>
      )}

      {loading ? (
        <div className="card text-muted text-[13px]">Caricamento…</div>
      ) : entries.length === 0 ? (
        <div className="card text-muted text-[13px] text-center py-8">
          Nessun evento corrisponde ai filtri.
        </div>
      ) : (
        <ul className="card p-0 divide-y divide-line">
          {entries.map((entry) => (
            <AuditEntry
              key={entry.id}
              entry={entry}
              expanded={expanded === entry.id}
              onToggle={() =>
                setExpanded((cur) => (cur === entry.id ? null : entry.id))
              }
              onCopyId={copyToClipboard}
            />
          ))}
        </ul>
      )}

      {hasMore && (
        <div className="flex justify-center">
          <button
            type="button"
            className="btn-secondary"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Caricamento…" : "Carica altri"}
          </button>
        </div>
      )}
    </div>
  );
}

/** Mappa l'`action` su un colore del puntino laterale.
 *  Heuristic: cerchiamo verbi noti (create/approve/reject/update/delete) come
 *  prefisso o suffisso. È volutamente lasca: tutto il resto cade in neutral. */
function dotClass(action: string): string {
  const a = action.toLowerCase();
  if (a.includes("delete") || a.includes("reject") || a.includes("cancel")) {
    return "bg-no";
  }
  if (a.includes("approve") || a.includes("resolve") || a.includes("settle")) {
    return "bg-yes";
  }
  if (a.includes("create") || a.includes("open")) {
    return "bg-accent";
  }
  if (a.includes("update") || a.includes("close") || a.includes("propose")) {
    return "bg-warn";
  }
  return "bg-bg-4";
}

function shortId(id: string | null): string {
  if (!id) return "—";
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function AuditEntry({
  entry,
  expanded,
  onToggle,
  onCopyId,
}: {
  entry: AuditLogView;
  expanded: boolean;
  onToggle: () => void;
  onCopyId: (id: string) => void;
}) {
  return (
    <li>
      <button
        type="button"
        className="w-full text-left px-4 py-3 hover:bg-bg-2 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-start gap-3">
          <div
            className={`mt-[6px] h-2 w-2 rounded-full flex-shrink-0 ${dotClass(entry.action)}`}
          />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
              <span className="font-mono text-text">{entry.action}</span>
              <span className="text-muted">·</span>
              <span className="text-text-2">{entry.target_kind}</span>
              {entry.target_id && (
                <span
                  className="font-mono text-[11px] text-muted"
                  title={entry.target_id}
                >
                  {shortId(entry.target_id)}
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted">
              <span
                className="font-mono"
                title={entry.actor_user_id ?? "system"}
              >
                {entry.actor_user_id
                  ? shortId(entry.actor_user_id)
                  : "system"}
              </span>
              <span>·</span>
              <span>{formatDateTime(entry.created_at)}</span>
            </div>
            {entry.reason && (
              <div className="mt-1 text-[12px] text-text-2">
                <span className="text-muted">reason: </span>
                {entry.reason}
              </div>
            )}
          </div>
          <span className="text-muted text-[11px]">
            {expanded ? "▾" : "▸"}
          </span>
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider text-muted">
              Payload
            </span>
            <button
              type="button"
              className="text-[11px] text-muted hover:text-text"
              onClick={(e) => {
                e.stopPropagation();
                onCopyId(entry.id);
              }}
            >
              Copia entry id
            </button>
          </div>
          <pre className="rounded-card border border-line bg-bg-2 p-3 font-mono text-[11.5px] text-text-2 overflow-auto max-h-[280px]">
            {safeStringify(entry.payload)}
          </pre>
        </div>
      )}
    </li>
  );
}

/** Stringify difensivo: il payload può contenere riferimenti circolari o
 *  oggetti nativi non serializzabili. In quel caso fallback al `String()`. */
function safeStringify(payload: unknown): string {
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
}
